import { MAX_SCREEN_TIME_LIMIT_SECONDS, MIN_SCREEN_TIME_LIMIT_SECONDS } from './countdown.js';
import { isScalePreset, SCALE_PRESETS, scalePoints } from './scale.js';
import {
  type FollowUp,
  MAX_OPTIONS_PER_QUESTION,
  MAX_QUESTIONS_PER_SCREEN,
  MIN_OPTIONS_PER_QUESTION,
  type Question,
  type Screen,
  type Theme,
} from './types.js';

/**
 * Structural rules from the spec, shared so the builder UI and the API agree
 * on what is valid rather than drifting apart.
 */

export interface ValidationIssue {
  path: string;
  message: string;
}

export const MAX_TITLE_LENGTH = 120;
export const MAX_QUESTION_LENGTH = 300;
export const MAX_OPTION_LENGTH = 150;
export const MAX_WORKSPACE_NAME_LENGTH = 80;
export const MAX_FOLDER_NAME_LENGTH = 80;

/**
 * Audience Q&A content caps (#118).
 *
 * Here beside the authoring caps rather than in qanda.ts, because these are
 * the numbers a reader scanning "how long may anything be" needs to find in
 * one place — and because the phone enforces the first of them before the API
 * does, which is the whole reason these live in `shared` at all.
 */
export const MAX_ASKED_QUESTION_LENGTH = 500;
export const MAX_ANSWER_LENGTH = 1000;
export const MAX_REJECTION_REASON_LENGTH = 200;

export const MAX_FOLLOWUP_MESSAGE_LENGTH = 280;
/** An authoring limit, not the browser's ~2048: real links fit well inside it,
 *  and it is what holds the structure item under its size budget. */
export const MAX_FOLLOWUP_URL_LENGTH = 512;
export const MAX_FOLLOWUP_LABEL_LENGTH = 40;
export const DEFAULT_FOLLOWUP_LABEL = 'Learn more';

/**
 * Tighter than the follow-up cap above, because a content image is never an
 * arbitrary link someone pasted: it is a key we minted ourselves, shaped
 * `media/quizzes/{quizId}/{kind}-{uuid}.{ext}`, which runs to about 145
 * characters under a long custom domain. 256 leaves room for a longer one
 * without letting URLs alone dominate the structure item — a fully packed
 * screen is 3 questions x 10 options, so the follow-up cap would have let
 * images take ~15 KB of the budget on its own.
 *
 * The same-origin rule that makes that shape a guarantee rather than an
 * expectation cannot live here: `shared` is imported by the taker's phone and
 * cannot read the API's env. It is enforced in the structure route.
 */
export const MAX_IMAGE_URL_LENGTH = 256;

export function validateTitle(title: string): ValidationIssue[] {
  const trimmed = title.trim();
  if (trimmed.length === 0) return [{ path: 'title', message: 'Title is required' }];
  if (trimmed.length > MAX_TITLE_LENGTH) {
    return [{ path: 'title', message: `Title must be ${MAX_TITLE_LENGTH} characters or fewer` }];
  }
  return [];
}

export function validateWorkspaceName(name: string): ValidationIssue[] {
  const trimmed = name.trim();
  if (trimmed.length === 0) return [{ path: 'name', message: 'Workspace name is required' }];
  if (trimmed.length > MAX_WORKSPACE_NAME_LENGTH) {
    return [
      {
        path: 'name',
        message: `Workspace name must be ${MAX_WORKSPACE_NAME_LENGTH} characters or fewer`,
      },
    ];
  }
  return [];
}

/**
 * A folder name (#76) — content, validated the way a poll title is.
 *
 * Deliberately not the `ID_PATTERN` rules below: those exist because an id ends
 * up concatenated into a sort key, and a folder's *id* is generated server-side
 * and validated that way. The name is only ever displayed.
 */
export function validateFolderName(name: string): ValidationIssue[] {
  const trimmed = name.trim();
  if (trimmed.length === 0) return [{ path: 'name', message: 'Folder name is required' }];
  if (trimmed.length > MAX_FOLDER_NAME_LENGTH) {
    return [
      {
        path: 'name',
        message: `Folder name must be ${MAX_FOLDER_NAME_LENGTH} characters or fewer`,
      },
    ];
  }
  return [];
}

/**
 * A question typed by someone in the room (#118).
 *
 * The path is `content` rather than `question` because "question" is
 * ambiguous in this codebase — a poll question is a different thing — and the
 * field is what the phone shows the error against.
 */
export function validateAskedQuestion(content: string): ValidationIssue[] {
  const trimmed = content.trim();
  if (trimmed.length === 0) return [{ path: 'content', message: 'A question is required' }];
  if (trimmed.length > MAX_ASKED_QUESTION_LENGTH) {
    return [
      {
        path: 'content',
        message: `A question must be ${MAX_ASKED_QUESTION_LENGTH} characters or fewer`,
      },
    ];
  }
  return [];
}

/**
 * A presenter's recorded answer (#118). Empty is rejected: no surface offers
 * clearing an answer, so an empty one is a client bug rather than an erasure.
 */
export function validateAnswer(answer: string): ValidationIssue[] {
  const trimmed = answer.trim();
  if (trimmed.length === 0) return [{ path: 'answer', message: 'An answer is required' }];
  if (trimmed.length > MAX_ANSWER_LENGTH) {
    return [
      { path: 'answer', message: `An answer must be ${MAX_ANSWER_LENGTH} characters or fewer` },
    ];
  }
  return [];
}

/**
 * A moderator's reason for turning a question down (#118). Optional — a
 * rejection with no reason is the ordinary case — so only length is checked.
 */
export function validateRejectionReason(reason: string): ValidationIssue[] {
  if (reason.trim().length > MAX_REJECTION_REASON_LENGTH) {
    return [
      {
        path: 'rejectionReason',
        message: `A reason must be ${MAX_REJECTION_REASON_LENGTH} characters or fewer`,
      },
    ];
  }
  return [];
}

/**
 * Authoring-time rules. Empty screens are legal in a draft (§4 Screen) — only
 * `validateReadyToPresent` requires every screen to hold at least one question.
 */
/**
 * Ids end up concatenated into DynamoDB sort keys, so a '#' inside one would
 * make the key ambiguous. They are generated by us, but they arrive from the
 * client on every save and must not be trusted.
 */
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function validateId(value: string, path: string, label: string): ValidationIssue[] {
  return ID_PATTERN.test(value)
    ? []
    : [{ path, message: `${label} must be 1-64 letters, digits, hyphens or underscores` }];
}

/**
 * The option rules, which the question's kind decides between (#50).
 *
 * A choice question's options are authored, so the rule is a count. A scale's
 * are generated from its preset, so the rule is that they *are* that generated
 * set — same length, same labels, same order. Enforcing the shape rather than a
 * count is what makes the preset trustworthy downstream: every reader derives a
 * point's value from its position, so a hand-edited or reordered option list
 * would move the mean without changing a single count.
 *
 * It is also why the 2-10 rule had to become type-dependent rather than merely
 * be raised — NPS is eleven points, and a choice question with eleven options
 * is still exactly as wrong as it was.
 */
function validateQuestionShape(question: Question, path: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (question.type !== undefined && question.type !== 'choice' && question.type !== 'scale') {
    // Nothing below can be checked against a kind we have no rules for, and
    // reporting it as a bad option list would name the wrong field.
    return [{ path, message: 'Unknown question type' }];
  }

  if (question.type !== 'scale') {
    if (question.scale !== undefined) {
      issues.push({ path, message: 'Only a rating question takes a scale' });
    }
    if (
      question.options.length < MIN_OPTIONS_PER_QUESTION ||
      question.options.length > MAX_OPTIONS_PER_QUESTION
    ) {
      issues.push({
        path,
        message: `A question needs between ${MIN_OPTIONS_PER_QUESTION} and ${MAX_OPTIONS_PER_QUESTION} options`,
      });
    }
    return issues;
  }

  if (!isScalePreset(question.scale)) {
    return [{ path, message: 'A rating question needs a scale' }];
  }

  // One rating per person. `buildQuestionResults` takes a single-choice
  // question's denominator by summing its option counts, and that sum is only
  // the number of people who rated while one device contributes one count.
  if (question.multiSelect === true) {
    issues.push({ path, message: 'A rating question takes one rating per person' });
  }

  const points = scalePoints(question.scale);
  const labels = question.options.map((option) => option.text.trim());
  if (
    labels.length !== points.length ||
    points.some((point, index) => labels[index] !== String(point))
  ) {
    const { label, min, max } = SCALE_PRESETS[question.scale];
    issues.push({
      path,
      message: `A ${label} question's options are generated: ${min} to ${max}, in order`,
    });
  }

  return issues;
}

/** Whether every field is blank, which is the same as having no follow-up. */
export function isEmptyFollowUp(followUp: FollowUp | null | undefined): boolean {
  if (!followUp) return true;
  return !(followUp.message?.trim() || followUp.url?.trim() || followUp.linkLabel?.trim());
}

/**
 * An https allowlist rather than a blocklist of dangerous schemes: `javascript:`
 * and `data:` are rejected for not being https, so a scheme nobody thought of
 * is rejected too. Relative and protocol-relative URLs throw here and fail the
 * same way.
 */
function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * The rules an image URL on a question or an option has to pass (#75).
 *
 * The same https allowlist the follow-up link uses, so `data:` — the one way to
 * put actual bytes into the structure item — is rejected for not being https
 * rather than by a rule of its own.
 */
export function validateImageUrl(value: string, path: string): ValidationIssue[] {
  const url = value.trim();
  if (url.length === 0) return [];

  if (url.length > MAX_IMAGE_URL_LENGTH) {
    return [{ path, message: `Image link must be ${MAX_IMAGE_URL_LENGTH} characters or fewer` }];
  }
  if (!isHttpsUrl(url)) {
    return [{ path, message: 'Image links must start with https://' }];
  }
  return [];
}

export function validateFollowUp(followUp: FollowUp, path: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const message = followUp.message?.trim() ?? '';
  const url = followUp.url?.trim() ?? '';
  const label = followUp.linkLabel?.trim() ?? '';

  if (message.length > MAX_FOLLOWUP_MESSAGE_LENGTH) {
    issues.push({
      path: `${path}.message`,
      message: `Follow-up message must be ${MAX_FOLLOWUP_MESSAGE_LENGTH} characters or fewer`,
    });
  }

  if (url.length > MAX_FOLLOWUP_URL_LENGTH) {
    issues.push({
      path: `${path}.url`,
      message: `Link must be ${MAX_FOLLOWUP_URL_LENGTH} characters or fewer`,
    });
  } else if (url.length > 0 && !isHttpsUrl(url)) {
    issues.push({ path: `${path}.url`, message: 'Links must start with https://' });
  }

  if (label.length > MAX_FOLLOWUP_LABEL_LENGTH) {
    issues.push({
      path: `${path}.linkLabel`,
      message: `Link label must be ${MAX_FOLLOWUP_LABEL_LENGTH} characters or fewer`,
    });
  } else if (label.length > 0 && url.length === 0) {
    issues.push({ path: `${path}.linkLabel`, message: 'A link label needs a link' });
  }

  return issues;
}

export function validateScreens(screens: Screen[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const seenScreenIds = new Set<string>();
  const seenQuestionIds = new Set<string>();

  screens.forEach((screen, screenIndex) => {
    const screenPath = `screens[${screenIndex}]`;

    issues.push(...validateId(screen.screenId, screenPath, 'Screen id'));
    if (seenScreenIds.has(screen.screenId)) {
      issues.push({ path: screenPath, message: 'Duplicate screen id' });
    }
    seenScreenIds.add(screen.screenId);

    if (screen.questions.length > MAX_QUESTIONS_PER_SCREEN) {
      issues.push({
        path: screenPath,
        message: `A screen holds at most ${MAX_QUESTIONS_PER_SCREEN} questions`,
      });
    }

    // #77 — the countdown's authored half. Absent is the normal case and the
    // only one every existing poll has; anything present has to be a whole
    // number of seconds inside the bounds, because it is what a deadline is
    // computed from on every advance.
    const limit = screen.timeLimitSeconds;
    if (
      limit !== undefined &&
      (typeof limit !== 'number' ||
        !Number.isInteger(limit) ||
        limit < MIN_SCREEN_TIME_LIMIT_SECONDS ||
        limit > MAX_SCREEN_TIME_LIMIT_SECONDS)
    ) {
      issues.push({
        path: screenPath,
        message: `A screen's time limit must be a whole number of seconds between ${MIN_SCREEN_TIME_LIMIT_SECONDS} and ${MAX_SCREEN_TIME_LIMIT_SECONDS}`,
      });
    }

    screen.questions.forEach((question, questionIndex) => {
      const questionPath = `${screenPath}.questions[${questionIndex}]`;

      issues.push(...validateId(question.questionId, questionPath, 'Question id'));
      if (seenQuestionIds.has(question.questionId)) {
        issues.push({ path: questionPath, message: 'Duplicate question id' });
      }
      seenQuestionIds.add(question.questionId);

      const questionText = question.text.trim();
      if (questionText.length === 0) {
        issues.push({ path: questionPath, message: 'Question text is required' });
      } else if (questionText.length > MAX_QUESTION_LENGTH) {
        issues.push({
          path: questionPath,
          message: `Question must be ${MAX_QUESTION_LENGTH} characters or fewer`,
        });
      }

      issues.push(...validateQuestionShape(question, questionPath));

      if (question.imageUrl !== undefined) {
        issues.push(...validateImageUrl(question.imageUrl, `${questionPath}.imageUrl`));
      }

      const seenOptionIds = new Set<string>();
      question.options.forEach((option, optionIndex) => {
        const optionPath = `${questionPath}.options[${optionIndex}]`;

        issues.push(...validateId(option.optionId, optionPath, 'Option id'));
        if (seenOptionIds.has(option.optionId)) {
          issues.push({ path: optionPath, message: 'Duplicate option id' });
        }
        seenOptionIds.add(option.optionId);

        const optionText = option.text.trim();
        if (optionText.length === 0) {
          issues.push({ path: optionPath, message: 'Option text is required' });
        } else if (optionText.length > MAX_OPTION_LENGTH) {
          issues.push({
            path: optionPath,
            message: `Option must be ${MAX_OPTION_LENGTH} characters or fewer`,
          });
        }

        if (option.imageUrl !== undefined) {
          issues.push(...validateImageUrl(option.imageUrl, `${optionPath}.imageUrl`));
        }

        // Bound to a local so TypeScript narrows away the null and undefined —
        // isEmptyFollowUp returning false does not narrow the property itself.
        const followUp = option.followUp;
        if (followUp && !isEmptyFollowUp(followUp)) {
          issues.push(...validateFollowUp(followUp, `${optionPath}.followUp`));
        }
      });
    });
  });

  return issues;
}

/**
 * Gate for starting a presentation (§5): every screen must hold 1–3 questions,
 * and the quiz must have at least one screen.
 */
export function validateReadyToPresent(screens: Screen[]): ValidationIssue[] {
  const issues = validateScreens(screens);

  if (screens.length === 0) {
    issues.push({ path: 'screens', message: 'Add at least one screen before presenting' });
  }

  screens.forEach((screen, screenIndex) => {
    if (screen.questions.length === 0) {
      issues.push({
        path: `screens[${screenIndex}]`,
        message: 'Every screen needs at least one question before presenting',
      });
    }
  });

  return issues;
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export function validateTheme(theme: Theme): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const colorFields: (keyof Theme)[] = [
    'primaryColor',
    'accentColor',
    'backgroundColor',
    'textColor',
  ];

  for (const field of colorFields) {
    const value = theme[field];
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
      issues.push({ path: field, message: 'Must be a hex color such as #1D4ED8' });
    }
  }

  // Kept out of the colorFields loop above: this is the only non-colour field
  // on Theme, and it is the enforcement point for the value. The quiz PATCH
  // handler spreads body.theme over DEFAULT_THEME without inspecting it.
  const { questionColumns } = theme;
  if (questionColumns !== undefined && ![1, 2, 3].includes(questionColumns)) {
    issues.push({ path: 'questionColumns', message: 'Must be 1, 2 or 3' });
  }

  return issues;
}

/**
 * The stage look every quiz starts with: tungsten amber on a warm black, the
 * palette of a filament lamp against a dark house. High contrast on projectors.
 */
export const DEFAULT_THEME: Theme = {
  primaryColor: '#F5A623',
  accentColor: '#8F7BFF',
  backgroundColor: '#131110',
  textColor: '#F7F3EA',
  backgroundImageUrl: null,
  logoUrl: null,
  questionColumns: 1,
};

/**
 * The serialized structure must fit one DynamoDB item (400 KB). The 100 KB of
 * headroom covers the theme's two URLs, the item envelope, and DynamoDB's own
 * attribute-name overhead.
 *
 * A size budget rather than a screen cap, because it measures what is actually
 * there: forty short screens are fine and twelve maximal ones are not.
 */
export const MAX_STRUCTURE_BYTES = 300 * 1024;
export const STRUCTURE_WARN_BYTES = Math.floor(MAX_STRUCTURE_BYTES * 0.8);

/**
 * `theme` is optional only because some callers have none to give — tests,
 * mainly; both production call sites (the save route, the editor) always pass
 * one. Omitting it under-measures the real payload by the theme's own size,
 * which the 100 KB of headroom above exists to absorb.
 */
export function structureBytes(screens: Screen[], theme?: Theme): number {
  return new TextEncoder().encode(JSON.stringify({ screens, theme })).length;
}

/**
 * `subject` picks only the message text and the issue's `path`, never the
 * budget itself: the structure route measures new screens against the
 * *persisted* theme, and the theme route measures a new theme against the
 * *persisted* screens, so whichever field the caller is actually writing is
 * the one an author can act on. Reusing the screens-flavoured message on the
 * theme route would send someone hunting through follow-up text that was
 * never the problem — and "split it into two polls" is worse than vague
 * there, since every resulting poll would still carry the same oversized
 * logo or background image.
 */
export function validateStructureSize(
  screens: Screen[],
  theme?: Theme,
  subject: 'screens' | 'theme' = 'screens',
): ValidationIssue[] {
  if (structureBytes(screens, theme) <= MAX_STRUCTURE_BYTES) return [];
  const message =
    subject === 'theme'
      ? 'This poll is too large to save. Shorten or remove the logo or background image.'
      : 'This poll is too large to save. Shorten follow-up messages, remove some question or option images, or split it into two polls.';
  return [{ path: subject, message }];
}
